
import React from "react";
import { formatCurrency } from '../../utils/calculator';
import { InfoTooltip } from '../../ui/InfoTooltip';
import { EuroIcon, ShieldCheckIcon } from 'lucide-react';

interface NursingHomeCostSummaryProps {
  nursingHomeCost: number;
  actualWeeklyPayment: number;
  weeklyPaymentWithLoan: number;
}

export function NursingHomeCostSummary({
  nursingHomeCost,
  actualWeeklyPayment,
  weeklyPaymentWithLoan
}: NursingHomeCostSummaryProps) {
  // Ensure actual payment is properly capped
  const cappedPayment = Math.min(weeklyPaymentWithLoan, nursingHomeCost);
  const stateSupport = nursingHomeCost - cappedPayment;
  const isCapped = weeklyPaymentWithLoan > nursingHomeCost;
  
  return (
    <div className="p-6 rounded-lg border bg-white shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-gray-900">Who Pays What</h4>
          <InfoTooltip content="This shows how the total nursing home cost is divided between your contribution and state support." />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
          <div className="flex items-center gap-2">
            <EuroIcon className="h-5 w-5 text-gray-700" />
            <p className="font-medium">Total Nursing Home Cost</p>
          </div>
          <p className="font-semibold text-gray-900">{formatCurrency(nursingHomeCost)}/week</p>
        </div>
        
        <div className="space-y-3 border-t border-b py-3">
          <h5 className="text-sm text-gray-600 mb-2">Split between:</h5>
          
          <div className="flex items-center justify-between py-2 bg-green-50 p-3 rounded-md">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <p className="font-medium">Your Contribution</p>
            </div>
            <p className="font-semibold text-green-700">
              {formatCurrency(cappedPayment)}/week
            </p>
          </div>
          
          <div className="flex items-center justify-between py-2 bg-blue-50 p-3 rounded-md">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <p className="font-medium">State Support (Fair Deal)</p>
            </div>
            <p className="font-semibold text-blue-700">
              {formatCurrency(stateSupport)}/week
            </p>
          </div>
        </div>
        
        <div className="bg-gray-50 p-3 rounded-md flex items-center justify-between">
          <p className="font-medium">Total Cost Covered</p>
          <p className="font-semibold">{formatCurrency(nursingHomeCost)}/week</p>
        </div>
      </div>
      
      {isCapped && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Your means assessment ({formatCurrency(weeklyPaymentWithLoan)}/week) exceeds the nursing home cost. 
              Under Fair Deal rules, your contribution is capped at {formatCurrency(nursingHomeCost)}/week,
              saving you {formatCurrency(weeklyPaymentWithLoan - nursingHomeCost)}/week.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
